import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

interface ConfirmDeleteModalProps {
  isOpen: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  userEmail?: string;
  title?: string;
  message?: string;
  isLoading?: boolean;
  confirmButtonText?: string;
  confirmButtonVariant?: 'destructive' | 'default' | 'outline';
}

export const ConfirmDeleteModal: React.FC<ConfirmDeleteModalProps> = ({
  isOpen,
  onConfirm,
  onCancel,
  userEmail,
  title = 'Delete User',
  message,
  isLoading = false,
  confirmButtonText,
  confirmButtonVariant = 'destructive',
}) => {
  if (!isOpen) return null;

  const defaultMessage = userEmail
    ? `Are you sure you want to delete user "${userEmail}"? This action cannot be undone.`
    : 'Are you sure you want to delete this user? This action cannot be undone.';

  // Determine button text based on title or use provided text
  const getButtonText = () => {
    if (confirmButtonText) return confirmButtonText;
    // Check for "deactivate" first (before "activate") to avoid matching "activate" in "deactivate"
    if (title.toLowerCase().includes('deactivate')) return isLoading ? 'Deactivating...' : 'Deactivate';
    if (title.toLowerCase().includes('activate')) return isLoading ? 'Activating...' : 'Activate';
    return isLoading ? 'Deleting...' : 'Delete';
  };

  return (
    <div className="fixed inset-0 bg-white/30 backdrop-blur-md flex items-center justify-center z-50">
      <Card className="w-full max-w-md mx-4 shadow-xl">
        <CardHeader>
          <CardTitle className={confirmButtonVariant === 'destructive' ? 'text-red-600' : 'text-gray-900'}>
            {title}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-gray-700 mb-6">
            {message || defaultMessage}
          </p>
          <div className="flex gap-2">
            <Button
              type="button"
              variant={confirmButtonVariant}
              onClick={onConfirm}
              disabled={isLoading}
              className="flex-1"
            >
              {getButtonText()}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={isLoading}
              className="flex-1"
            >
              Cancel
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};


